import { renderContractHtml } from "../patients/contract-html";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";

import {
  Eye,
  File,
  Loader,
  Paperclip,
  Search,
  Trash,
  WandSparkles,
  CheckCircle,
} from "lucide-react";

import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Pagination } from "../../pagination";
import { AlertDialogDelete, DialogResponsive } from "../../dialogs";
import { Skeleton } from "@/components/ui/skeleton";

import { Employee } from "@/types";
import {
  useCreateContractMutation,
  useFetchContractPDFMutation,
  useFetchContractsQuery,
  useFetchModelsForContractQuery,
  useToast,
  useDebounce,
} from "@/hooks";

import { DisplayError, formatDate, truncateText } from "@/utils";
type FormContractCollaboratorProps = {
  collaborator: Employee;
};

export function FormContractCollaborator({
  collaborator,
}: FormContractCollaboratorProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);

  const collaboratorId = collaborator.id;
  const [searchParams] = useSearchParams();
  const contractPatientPage = searchParams.get("contract_collaborator_page");

  const [selectedValue, setSelectedValue] = useState("");

  const { mutateAsync, isLoading: creatContractIsLoading } =
    useCreateContractMutation();
  const { data: modelsData, isLoading: isLoadingModels } =
    useFetchModelsForContractQuery("employee", debouncedSearch);
  const { mutateAsync: download, isLoading: isLoadingDownload } =
    useFetchContractPDFMutation();
  const {
    data: contractsPatientData,
    refetch,
    isLoading: isLoadingContractsPatientData,
  } = useFetchContractsQuery({
    id: collaboratorId,
    contractableType: "Employee",
    page: contractPatientPage,
  });

  const validation = Yup.object().shape({
    model_id: Yup.string().min(3, t("LABEL.min")).required(t("LABEL.required")),
  });

  const initialValues = {
    contractable_id: collaborator.id,
    contractable_type: "Employee",
    start_date: "",
    model_id: "",
  };

  const { handleSubmit, isSubmitting, setSubmitting, resetForm, ...formik } =
    useFormik({
      initialValues,
      validationSchema: validation,
      onSubmit: async (values) => {
        handleCreateContractSubmit(values);
      },
    });

  async function handleCreateContractSubmit(request: any) {
    try {
      await mutateAsync(request, {
        onSuccess: () => {
          resetForm();
          toast({
            title: t("LABEL.success"),
            description: t("MESSAGE.success"),
          });
        },
        onError: (error: any) => {
          toast({
            title: t("LABEL.error"),
            description: DisplayError(error, t("MESSAGE.error_message")),
            variant: "destructive",
          });
        },
      });

    } catch {
      toast({
        title: t("LABEL.error"),
        description: t("MESSAGE.error"),
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  }

  function handleSetSelectedValue(value: any) {
    formik.setFieldValue("model_id", value);
    setSelectedValue(value);
  }

  const handleDownload = (id: string) => {
    download(
      { id },
      {
        onError: (error: any) => {
          toast({
            title: t("LABEL.error"),
            description: DisplayError(error, t("MESSAGE.error_message")),
            variant: "destructive",
          });
        },
      }
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      await refetch();
    };

    fetchData();
  }, [refetch, contractsPatientData, contractPatientPage]);

  return (
    <section className="w-full space-y-2 p-4">
      <div className="flex items-center justify-end">
        <Popover>
          <PopoverTrigger>
            <Button>
              <WandSparkles size={14} />
              <span>Gerar contrato</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="mr-28 max-w-md w-full p-0">
            <form onSubmit={handleSubmit}>
              <div className="rounded-xl border border-app-border bg-app-surface overflow-hidden">
                <div className="border-b border-app-border px-4 py-3">
                  <h2 className="font-display text-base text-app-fg-strong flex items-center gap-2">
                    <File className="h-5 w-5 text-app-fg-muted" aria-hidden="true" />
                    Selecionar Modelo
                  </h2>
                  <p className="text-sm text-app-fg-muted mt-1">
                    Buscar modelo de contrato
                  </p>
                </div>

                <div className="p-4 border-b border-app-border">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-app-fg-subtle" />
                    <Input
                      type="search"
                      placeholder="Buscar por nome do modelo..."
                      className="w-full pl-10"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                <div className="max-h-[300px] overflow-y-auto">
                  {isLoadingModels && !modelsData ? (
                    <div className="p-4 space-y-2">
                      {Array.from({ length: 6 }).map((_, index) => (
                        <Skeleton key={index} className="h-14 w-full rounded-xl" />
                      ))}
                    </div>
                  ) : modelsData?.items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <File className="h-10 w-10 text-app-fg-subtle mb-2" aria-hidden="true" />
                      <p className="text-app-fg-muted text-sm">Nenhum modelo encontrado</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-app-border-subtle">
                      {modelsData?.items.map((item) => (
                        <div
                          key={item.id}
                          onClick={() => handleSetSelectedValue(item.id)}
                          className={`
                            flex items-center gap-3 p-4 cursor-pointer transition-colors
                            hover:bg-app-surface-muted
                            ${item.id === selectedValue
                              ? "bg-brand-soft border-l-4 border-brand"
                              : "border-l-4 border-transparent"
                            }
                          `}
                        >
                          <File className={`h-5 w-5 ${item.id === selectedValue ? "text-brand" : "text-app-fg-muted"}`} />
                          <div className="flex-1">
                            <p className="font-medium text-app-fg text-sm">
                              {truncateText(item.description, 40)}
                            </p>
                            <p className="text-xs text-app-fg-muted">
                              {item.modelType?.name}
                            </p>
                          </div>
                          {item.id === selectedValue && (
                            <CheckCircle className="text-brand h-5 w-5" aria-hidden="true" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="p-4 border-t border-app-border">
                  <Button
                    className="w-full"
                    disabled={isSubmitting || creatContractIsLoading || !selectedValue}
                    isLoading={isSubmitting || creatContractIsLoading}
                  >
                    Gerar contrato
                  </Button>
                </div>
              </div>
            </form>
          </PopoverContent>
        </Popover>
      </div>
      <div className="rounded-xl border border-app-border bg-app-surface overflow-hidden">
        <div className="max-h-[500px] overflow-auto">
          <Table className="border-separate border-spacing-0">
            {contractsPatientData && contractsPatientData.items.length <= 0 && (
              <TableCaption className="py-8 text-app-fg-muted">
                {t("LABEL.no results")}
              </TableCaption>
            )}
            <TableHeader className="sticky top-0 z-20 bg-app-surface-muted">
              <TableRow>
                <TableHead className="text-app-fg-muted font-semibold text-sm uppercase tracking-wide py-4">
                  {t("LABEL.model type")}
                </TableHead>
                <TableHead className="text-app-fg-muted font-semibold text-sm uppercase tracking-wide py-4">
                  {t("LABEL.created_at")}
                </TableHead>
                <TableHead className="text-app-fg-muted font-semibold text-sm uppercase tracking-wide py-4 w-[180px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contractsPatientData && !isLoadingContractsPatientData ? (
                <>
                  {contractsPatientData.items.map((item, index) => (
                    <TableRow
                      key={index}
                      className="transition-colors hover:bg-app-surface-muted data-[state=selected]:bg-brand-soft"
                    >
                      <TableCell className="py-4 px-4 text-sm text-app-fg font-medium">
                        {item.contractable_type}
                      </TableCell>
                      <TableCell className="py-4 px-4 text-sm text-app-fg font-medium">
                        {formatDate(item.start_date, false, true)}
                      </TableCell>
                      <TableCell className="py-4 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <DialogResponsive
                            title={`Contrato do colaborador ${
                              collaborator.person.name.split(" ")[0]
                            }`}
                            description="Leia os termos do contrato do colaborador."
                            defaultOpen={open}
                            setDefaultOpen={setOpen}
                            className="max-h-[500px] overflow-auto"
                            button={
                              <Button
                                variant="ghost"
                                size="sm"
                                className="justify-center"
                                aria-label="Ver os termos do contrato"
                                title="Ver os termos do contrato"
                              >
                                <Eye size={18} aria-hidden="true" />
                              </Button>
                            }
                          >
                            <section className="border rounded-lg h-[30vh] overflow-auto">
                              <ContractPreview htmlContentData={item.terms} />
                            </section>
                          </DialogResponsive>

                          <Button
                            variant="ghost"
                            size="sm"
                            className="justify-center text-brand"
                            aria-label="Baixar o contrato em PDF"
                            title="Baixar o contrato em PDF"
                            onClick={() => handleDownload(item.id)}
                            disabled={isLoadingDownload}
                          >
                            {isLoadingDownload ? (
                              <Loader
                                size={16}
                                className="h-4 w-4 animate-spin"
                              />
                            ) : (
                              <Paperclip size={18} />
                            )}
                          </Button>
                          <AlertDialogDelete
                            endpoint={`/contracts/${item.id}`}
                            queryKey="contracts"
                          >
                            <Button
                              variant="ghost"
                              size="sm"
                              className="justify-center text-app-danger"
                              aria-label="Excluir o contrato"
                              title="Excluir o contrato"
                            >
                              <Trash size={16} aria-hidden="true" />
                            </Button>
                          </AlertDialogDelete>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              ) : (
                Array.from({ length: 10 }).map((_, index) => (
                  <TableRow
                    key={index}
                    className="hover:bg-app-surface-muted"
                  >
                    <TableCell className="py-4 px-4">
                      <Skeleton className="w-full h-10" />
                    </TableCell>
                    <TableCell className="py-4 px-4">
                      <Skeleton className="w-full h-10" />
                    </TableCell>
                    <TableCell className="py-4 px-4">
                      <Skeleton className="w-full h-10" />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {contractsPatientData && (
        <Pagination
          data={contractsPatientData.data}
          type="custom"
          type_string="contract_collaborator_page"
        />
      )}
    </section>
  );
}

interface ContractPreviewProps {
  htmlContentData: string;
}

function ContractPreview({ htmlContentData }: ContractPreviewProps) {
  return <div className="contract-rich-text" dangerouslySetInnerHTML={{ __html: renderContractHtml(htmlContentData) }} />;
}
